/**
 * The taxing regimes a jurisdiction can be on.
 *
 * MIRROR of `TAX_SYSTEMS` in `fieldiq-api/src/lib/company-tax.ts`, which is
 * itself pinned to the Postgres `tax_system` enum. Same relationship
 * `timezones.ts` has to the API's zone allowlist: the API is the source of
 * truth, this file exists so the browser stops carrying its own answer, and
 * the test beside it is what stops the copy drifting.
 *
 * MEMBERSHIP, NOT PLAUSIBILITY. The API's list carried a sixth value, `none`,
 * which is not in the Postgres enum — selecting it produced a raw
 * `22P02 invalid input value for enum tax_system` rather than a 400, so a
 * legitimate-looking dropdown choice was a guaranteed 500. `US_exempt` is how
 * "no tax" is expressed. Do not add a value here that the enum does not hold.
 */

export const TAX_SYSTEMS = ['GST_only', 'GST_PST', 'HST', 'US_state', 'US_exempt'] as const;

export type TaxSystem = (typeof TAX_SYSTEMS)[number];

/**
 * Exhaustive, keyed on every enum value — no binary check, no raw echo. A
 * label map with a guessing fallback is how a display bug gets reported as a
 * data bug (CLAUDE.md, "Label maps must be exhaustive, never binary").
 */
export const TAX_SYSTEM_LABELS: Record<TaxSystem, string> = {
  GST_only: 'GST only',
  GST_PST: 'GST + PST',
  HST: 'HST',
  US_state: 'State tax',
  US_exempt: 'No tax (exempt)',
};

export function isTaxSystem(value: unknown): value is TaxSystem {
  return typeof value === 'string' && (TAX_SYSTEMS as readonly string[]).includes(value);
}

/**
 * Display a stored value, including the two cases that are not a system:
 * absent renders as an em dash, and a value the enum does not hold renders as
 * `Unknown (x)` so drift is visible on screen instead of wearing a plausible
 * label. Never returns ''.
 */
export function taxSystemLabel(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (isTaxSystem(value)) return TAX_SYSTEM_LABELS[value];
  return `Unknown (${String(value)})`;
}

/**
 * Which rate a system REQUIRES, mirroring `checkTaxCoherence` in the API.
 *
 * `US_exempt` is deliberately absent from the taxing set: it is complete with
 * no rate at all. Every other system needs rate 1, and GST_PST needs rate 2 as
 * well — a GST_PST row saved with only a GST rate resolves as *unconfigured*,
 * so the document would carry "Tax rate not configured" while the settings
 * screen showed a successful save.
 *
 * `tax-systems.test.ts` walks all five values, so adding a system without
 * deciding this fails there rather than shipping a row the API refuses.
 */
export function requiredRates(system: TaxSystem): { rate1: boolean; rate2: boolean } {
  switch (system) {
    case 'GST_PST': return { rate1: true, rate2: true };
    case 'GST_only':
    case 'HST':
    case 'US_state': return { rate1: true, rate2: false };
    case 'US_exempt': return { rate1: false, rate2: false };
  }
}

/**
 * The label the API prints for a rate when the row leaves the label blank —
 * `defaultLabels` in `company-tax.ts`. Shown as a PLACEHOLDER on the form, not
 * as a value: a blank label column really does render as this downstream, and
 * stating that is documentation rather than a suggested answer.
 *
 * Note this is only about the two LABEL fields. The one place a RATE is
 * suggested is "Add jurisdiction" for a Canadian province, and it comes from
 * the API's statutory table (GET /settings/standard-tax-rates), not from here.
 */
export function defaultRateLabels(system: TaxSystem): { label1: string | null; label2: string | null } {
  switch (system) {
    case 'GST_PST': return { label1: 'GST', label2: 'PST' };
    case 'HST': return { label1: 'HST', label2: null };
    case 'GST_only': return { label1: 'GST', label2: null };
    case 'US_state': return { label1: 'State Tax', label2: null };
    case 'US_exempt': return { label1: null, label2: null };
  }
}
